import { formatNumber } from "@angular/common";
import { Injectable } from "@angular/core";
import Fields from '../_consts/fields';
import moment from "moment-timezone";
import { HttpPlus } from "./http-plus";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "src/environments/environment";
import { Constants } from "../_consts/constants";
import dateFormats from "./const/date-formats";
import { SharedService } from "../_shared/shared.service";
import { SObject } from "./objects/s-object";
import { HttpParams } from "@angular/common/http";

@Injectable({
  providedIn: 'root'
})
export class Fce {
  static numberLocale = "en-US"


  public static convertToStatic(obj, showCols, transService: TranslateService) {
    if (!obj)
      return;
    if (showCols) {
      for (const c in showCols) {
        const col = showCols[c];
        if (!obj.hasOwnProperty(col.attr))
          continue;
        obj[col.attr] = Fce.convertToStaticOne(obj[col.attr], col.type, col, transService)
      }
    } else {
      for (const key in obj) {
        obj[key] = Fce.convertToStaticOne(obj[key], Fields[key], null, transService)
      }
    }
    return obj;
  }

  public static convertToStaticOne(value, type, col, transService: TranslateService) {
    switch (type) {
      case "date":
        return value ? moment(value).format(dateFormats.DATE) : ''
      case "datetime":
        return value ? moment(value).format(dateFormats.DATETIME) : ''
      case "time":
        return value ? moment(value).format(dateFormats.TIME) : ''
      case "timestamp":
        return value ? moment.unix(value).format(dateFormats.SIMPLEDATETIME) : ''
      case "week":
        return value ? moment(value).format(dateFormats.WEEK) : ''
      case "ym":
        return value ? moment(value).format(dateFormats.YM) : ''
      case "integer":
        return formatNumber(value, Fce.numberLocale, '1.0-0');
      case "number":
        return formatNumber(value, Fce.numberLocale, '1.0-2');
      case "decimal":
        return formatNumber(value, Fce.numberLocale, '1.2-2');
      case "bool":
        return transService.instant(`bool_${value}`)
      case "translate":
        return value ? transService.instant(`${col?.transPrefix || ''}${value}`) : null
      case "hm":
        return Fce.secondsToHm(value)      
      case "mins":
        return Fce.secondsToHm((value??0)*60)
      case "prc":
        return `${value}%`
      case "prc1":
        return `${formatNumber(value, Fce.numberLocale, '1.0-1')}%`
    }
    return value
  }



  public static convertToStaticArray(array, showCols, transService: TranslateService) {
    for (const i in array) {
      array[i] = Fce.convertToStatic(array[i], showCols, transService);
    }

    return array;
  }


  public static convertForClient = function (obj, onlyFields = null) {
    if (obj && obj.constructor && obj.constructor.name == "Array") {
      const tmp = {};
      for (const key in obj) {
        tmp[key] = obj[key];
      }
      obj = tmp;
    }
    for (const key in obj) {
      if (onlyFields && !onlyFields.includes(key))
        continue;
      if (obj.hasOwnProperty(key)) {
        switch (Fields[key]) {
          case "bool":
            obj[key] = Fce.intToBoolean(obj[key]);
            break;
          case "dh":
          case "datetime":
          case "time":
          case "date":
          case "week":
            obj[key] = Fce.intToDate(obj[key]);
            break;
          case "timestamp":
            obj[key] = obj[key] ? ((obj[key] instanceof Date) ? obj[key] : moment.unix(obj[key]).toDate()) : null
            break;
          case "integer":
          case "number":
            obj[key] = Fce.stringToInt(obj[key]);
            break;
          case "json_array":
            if (Array.isArray(obj[key]))
              continue
            obj[key] = obj[key] ? JSON.parse(obj[key]) : [];
            break;
          case "json":
            if(obj[key] && typeof obj[key] === "object")
              continue;
            obj[key] = obj[key] ? JSON.parse(obj[key]) : {};
            break;
          case "array":
            obj[key] = obj[key] && obj[key].split ? obj[key].split(",") : [];
            break;
          case "array_integer":
            obj[key] = obj[key] && obj[key].split ? obj[key].split(",") : [];
            obj[key] = obj[key].map(v => parseInt(v))
            break;
        }
      }
    }
    return obj;
  }


  public static convertForClientArray = function (array: [any]) {
    for (const i in array) {
      array[i] = Fce.convertForClient(array[i]);
    }
    return array;
  }

  public static convertForServer = function (obj, onlyFields = null) {
    for (const key in obj) {
      if (onlyFields && !onlyFields.includes(key))
        continue;
      if (obj.hasOwnProperty(key)) {
        switch (Fields[key]) {
          case "bool":
            obj[key] = Fce.booleanToInt(obj[key]);
            break;
          case "dh":
          case "datetime":
          case "time":
          case "date":
          case "week":
            obj[key] = obj[key] ? moment(obj[key]).format(dateFormats.SERVER) : null
            break;
          case "timestamp":
            obj[key] = obj[key] ? moment(obj[key]).unix() : null
            break;
          case "integer":
          case "number":
            obj[key] = Fce.intToString(obj[key]);
            break;
          case "json_array":
            obj[key] = obj[key] ? JSON.stringify(obj[key], Fce.getCircularReplacer()) : null;
            break;
          case "json":
            obj[key] = obj[key] ? JSON.stringify(obj[key], Fce.getCircularReplacer()) : null;
            break;
          case "array":
          case "array_integer":
            obj[key] = obj[key].join ? obj[key].join(",") : obj[key];
            break;
        }

      }
    }
    return obj;
  }

  public static convertForServerArray = function (array) {
    for (const i in array) {
      if (array[i])
        array[i] = Fce.convertForServer(array[i]);
    }
    return array;
  }

  public static ensureArray(obj) {
    if (!obj)
      return null
    return Array.isArray(obj) ? obj : [obj]
  }

  public static removeNullParams = function (obj, removeFalse?) {
    for (const key in obj) {
      if (obj[key] == null || (obj[key] + "").length == 0 || (removeFalse && !obj[key]) || (typeof obj[key] === "object" && Object.keys(obj[key]).length == 0))
        delete obj[key];
    }
    return obj;
  }

  public static removeHtmlTags(input: string): string {
    return input.replace(/<\/?[^>]+(>|$)/g, "");
  }


  public static compareObjectsArray = function (x, y, skipKeys = null) {
    let result = true;
    if (x.length != y.length)
      return false;
    for (const i in x) {
      if (!Fce.compareObjects(x[i], y[i], skipKeys)) {
        result = false;
        break;
      }
    }
    return result;
  }

  public static compareObjects = function (x, y, skipKeys = null) {
    if (!skipKeys)
      skipKeys = [];
    if (x === null || x === undefined || y === null || y === undefined) {
      return x == y;
    }
    if (!(x instanceof SObject) && (x == y || x.valueOf() === y.valueOf())) {
      return true;
    }

    if (x instanceof Date || !(x instanceof Object) || y instanceof Date || !(y instanceof Object)) {
      return false;
    }

    const p = Object.keys(x);
    if (Object.keys(y).every(function (i) {
      if (skipKeys.includes(i))
        return true;
      return p.includes(i);
    })) {
      return p.every(function (i) {
        if (skipKeys.includes(i))
          return true;
        return Fce.compareObjects(x[i], y[i], skipKeys);
      })
    } else return false;
  }

  public static round = function (number, decPlaces = 2) {
    if (number) {
      if (!number.toFixed)
        number = parseFloat(number)
      return parseFloat(number.toFixed(decPlaces));
    }
    else return number;
  }


  public static dateDiffDays = function (dt1, dt2) {
    const diff = (Date.UTC(dt1.getFullYear(), dt1.getMonth(), dt1.getDate()) - Date.UTC(dt2.getFullYear(), dt2.getMonth(), dt2.getDate()));
    if (diff == 0) return 0;
    else
      return Math.floor(diff / (1000 * 60 * 60 * 24));
  }

  public static dateDiffSec = function (dt1, dt2) {
    const diff = (Date.UTC(dt1.getFullYear(), dt1.getMonth(), dt1.getDate(), dt1.getHours(), dt1.getMinutes(), dt1.getSeconds()) -
      Date.UTC(dt2.getFullYear(), dt2.getMonth(), dt2.getDate(), dt2.getHours(), dt2.getMinutes(), dt2.getSeconds()));
    if (diff == 0) return 0;
    else
      return Math.floor(diff / 1000);
  }

  public static isInt = function (n) {
    const num = Number(n);
    return !isNaN(num) && num % 1 === 0;
  }

  public static stringToInt = function (num) {
    if (Fce.isInt(num))
      return parseInt(num);
    else if (num)
      return parseFloat(num);
    return num;
  }

  public static intToString = function (num) {
    return num ? (num + "").replace(",", ".") : num;
  }

  public static intToBoolean = function (bool) {
    if (bool == 1 || bool == "1" || bool == true)
      return true;
    else return false;
  }

  public static booleanToInt = function (bool) {
    if (bool && bool !== "false" && bool != "0")
      return 1;
    else return 0;
  }

  public static parseBool = function (bool) {
    if (bool?.toLowerCase)
      bool = bool.toLowerCase()
    return (bool == 1 || bool == "1" || bool == true || bool == "true")
  }

  public static intToDate = function (date) {
    if (date) {
      return new Date(date);
    } else return null;
  }

  public static cropText = function (text, max) {
    if (text && `${text}`.length > max) {
      return `${text}`.substring(0, max) + '...';
    } else
      return text
  }

  public static dateToInt = function (date: Date) {
    if (date) {
      if (date instanceof Date) {
        return date.toISOString();
      } else {
        return date;
      }
    } else return null;
  }

  public static formatDate = function (date: Date | string, format = dateFormats.DATETIME) {
    //return moment(date).utc(true).local().format(format)
    return moment(date).format(format)
  }

  public static isWeekend = (date: Date) => {
    const dayOfWeek = date.getDay();
    return (dayOfWeek === 6) || (dayOfWeek === 0);
  }

  public static isHoliday(date: Date) {
    return Fce.getHolidays(date).includes(Fce.formatDate(date, "*-MM-DD"));
  }

  public static getHolidays(date: Date): Array<string> {
    const holidayDays = Constants.HOLIDAY_DAYS
    const year = date.getFullYear()
    const easter = `${year}-${Fce.getEaster(year)}`
    const easterMonday = new Date(easter)
    easterMonday.setDate(easterMonday.getDate() + 1)
    const easterFriday = new Date(easter)
    easterFriday.setDate(easterFriday.getDate() - 2)

    holidayDays.push(Fce.formatDate(easterMonday, '*-MM-DD'))
    holidayDays.push(Fce.formatDate(easterFriday, '*-MM-DD'))
    return holidayDays;
  }

  public static getEaster(year) {
    const f = Math.floor,
      // Golden Number - 1
      G = year % 19,
      C = f(year / 100),
      // related to Epact
      H = (C - f(C / 4) - f((8 * C + 13) / 25) + 19 * G + 15) % 30,
      // number of days from 21 March to the Paschal full moon
      I = H - f(H / 28) * (1 - f(29 / (H + 1)) * f((21 - G) / 11)),
      // weekday for the Paschal full moon
      J = (year + f(year / 4) + I + 2 - C + f(C / 4)) % 7,
      // number of days from 21 March to the Sunday on or before the Paschal full moon
      L = I - J,
      month = 3 + f((L + 40) / 44),
      day = L + 28 - 31 * f(month / 4);

    return `${month}-${day}`;
  }

  public static secondsToHm = function (seconds) {
    seconds = Number(seconds);
    let txt = seconds < 0 ? "-" : "";
    seconds = Math.abs(seconds);
    const h = Math.floor(seconds / 3600);
    const m = Math.floor(seconds % 3600 / 60);

    if (h != 0)
      txt += (h < 10 ? '0' + h : h) + "h" + " : ";
    txt += (m < 10 ? '0' + m : m) + "m";
    return txt;
  }

  public static empty = function (value) {
    return value == null || value == "undefined" || value?.length == 0 || (value + "").length == 0
  }

  public static getCircularReplacer = () => {
    const seen = new WeakSet();
    return (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return;
        }
        seen.add(value);
      }
      return value;
    };
  };

  public static copy = function (obj, removeCircular = false) {
    if (obj)
      return JSON.parse(JSON.stringify(obj, removeCircular ? Fce.getCircularReplacer() : null))

    else return null
  }

  public static copyOnly = function (objFrom, attr, objTo = {}) {
    if (objFrom)
      for (const a of attr) {
        objTo[a] = objFrom[a]
      }
    return objTo
  }

  public static makeNested = function (origArray, idAttr, idParentAttr, subarrayAttr) {
    if (origArray)
      for (let i = origArray.length - 1; i > -1; i--) {
        if (origArray[i][idParentAttr]) {
          const g = origArray.find(e => e[idAttr] == origArray[i][idParentAttr])
          if (g) {
            if (!g[subarrayAttr])
              g[subarrayAttr] = []
            g[subarrayAttr].unshift(origArray[i]);
          }
          origArray.splice(i, 1)
        }
      }
    return origArray
  }


  public static getFileHref(f, show: boolean = true, isTmp: boolean = false) {
    return `${environment.apiUrl}files/doc?path=${encodeURIComponent(f)}${show ? '&show=true' : ''}${isTmp ? '&tmp=true' : ''}`
  }
  public static getImgPreviewHref(f) {
    return `${environment.apiUrl}files/imagePreview?path=${encodeURIComponent(f)}&show=true`
  }

  public static resizeImage(base64Str, MAX_WIDTH, MAX_HEIGHT) {
    return new Promise((res, rej) => {
      const img = new Image();
      img.src = base64Str
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let width = img.width
        let height = img.height

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT
          }
        }
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)
        res(canvas.toDataURL())
      }
      img.onerror = error => rej(error);
    })
  }

  public static async download(http: HttpPlus, filenameOrResponse: string | any, showAsTab: boolean = false) {
    let filename: string, token: string = null;
    if (typeof filenameOrResponse === 'object') {
      ({ filename, token } = filenameOrResponse);
    } else {
      filename = filenameOrResponse;
    }

    let url: string;
    let isBlobUrl = false;

    if (token) {
      // Token systém - přímá URL bez auth
      const showParam = showAsTab ? '&show=true' : '';
      url = `${environment.apiUrl}files/docByToken?path=${encodeURIComponent(filename)}&token=${token}${showParam}`;
    } else {
      // Session auth - stáhnout jako blob
      const params: any = { path: filename }
      if (showAsTab)
        params.show = true
      const response: any = await http.get('files/tmpDoc', params, false, { responseType: 'blob', observe: 'response' });
      if (!response?.body) {
        console.error('Download error: No response body found');
        return;
      }

      // Kontrola chybové odpovědi
      if (!response.ok || response.status >= 400) {
        const errorText = await response.body.text();
        console.error('Download error:', errorText);
        alert(errorText || 'Download failed');
        return;
      }

      url = URL.createObjectURL(response.body);
      isBlobUrl = true;
    }

    // Otevřít nebo stáhnout
    if (showAsTab && this.tryOpenInNewTab(url)) {
      if (isBlobUrl) setTimeout(() => URL.revokeObjectURL(url), 1000);
      return;
    }

    // Stažení souboru
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    if (isBlobUrl) URL.revokeObjectURL(url);
  }

  private static tryOpenInNewTab(url: string): boolean {
    const newTab = window.open(url, '_blank');
    if (!newTab || newTab.closed || typeof newTab.closed === 'undefined') {
      console.warn('Opening in new tab was blocked, falling back to download');
      return false;
    }
    return true;
  }

  public static getQrCodeUrl(data, text = null, type = "QRCODE") {
    let params = new HttpParams()
      .set('data', data)
      .set('type', type);
    if (text) {
      params = params.set('text', text);
    }

    return `${environment.apiUrl}files/generateQrCode?${params.toString()}`;
  }

  public static async openQrCodeNewTab(http: HttpPlus, shared: SharedService, data, text = null, type = "QRCODE") {
    let params: any = {
      data,
      type
    }
    if (text)
      params.text = text;


    const html: any = await http.get('files/generateQrCode', params, false, { responseType: 'text' })
    console.log(html)
    if (html) {
      const newWindow = window.open('', '_blank');
      if (!newWindow) {
        shared.warning("New window tab blocked by browser!");
        return;
      }
      newWindow.document.open();
      newWindow.document.write(html);
      newWindow.document.close();
    }
  }

  public static filterColsBySFU(cols: any[], isSfu: boolean) {//Legacy
    return cols.filter(c => !c.hasOwnProperty('sfu') || c.sfu == isSfu)
  }

  public static async saveExtra(row, http, shared: SharedService) {//Legacy
    const params = Fce.convertForServer(Fce.copy(row))
    console.log(params)
    const response: any = await http.post(`saveExtra`, params)
    if (response) {
      shared.success()
      return response
    }
  }

}